import { READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import { OverlayReadinessSignals, OverlayReadinessState } from 'modules/overlay-v2/domain/interfaces/readiness';
import { buildReadinessSteps } from 'modules/overlay-v2/domain/utils/build-readiness-steps';
import { selectGameInfoError } from 'modules/overlay-v2/store/game-info/selectors';
import { selectIsBroadcastInitialized, selectIsMatchAvailable } from 'modules/overlay-v2/store/overlay/selectors';
import { retryCancelled, retryRequested, retryScheduled, wentLive } from 'modules/overlay-v2/store/readiness/readiness';
import {
  selectAssetsTask,
  selectChronologyTask,
  selectDeviceTask,
  selectHasGoneLive,
  selectMatchTimeTask,
  selectReadinessRetry,
  selectRetryAt,
  selectRetryToken,
  selectTemplateTask,
} from 'modules/overlay-v2/store/readiness/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import useRemoteConnectionStep from 'modules/overlay-v2/ui/hooks/use-remote-connection-step';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface OverlayReadiness extends OverlayReadinessState {
  /** Повторить подготовку прямо сейчас, не дожидаясь автоматического повтора. */
  onRetry: () => void;
  /**
   * Показывать ли экран подготовки. Держится ещё `READINESS_EXIT_DURATION_MS` после
   * готовности, чтобы чеклист успел показать «Всё готово» и уйти анимацией,
   * а не сменился табло рывком. После выхода в эфир — всегда `false`.
   */
  isScreenVisible: boolean;
  /**
   * Монтировать ли табло и панель.
   *
   * Это **не** `isReady`. `isReady` — мгновенное «прямо сейчас все сигналы сошлись», и
   * монтировать по нему нельзя: любая пересинхронизация, моргнувшая сеть или нажатая
   * оператором кнопка роняли его в ноль и пересоздавали канвы Fabric прямо в эфире.
   * `isOnAir` — однонаправленное «подготовка этого матча уже прошла», и снимается оно
   * только вместе со сменой матча.
   */
  isOnAir: boolean;
}

/**
 * Чеклист подготовки трансляции для оверлея и панели управления.
 *
 * Хук только собирает сигналы из тех мест, где они уже живут, — саму лестницу считает
 * чистая `buildReadinessSteps`. Своё состояние здесь минимально: три шага, которым
 * негде было храниться (журнал матча, время, картинки), лежат в слайсе `readiness`.
 */
function useOverlayReadiness(): OverlayReadiness {
  const dispatch = useAppDispatch();

  const { organizerId, error: routeError } = useOverlayRouteParams();

  const {
    data: feature,
    isLoading: isFeatureLoading,
    error: featureError,
    refetch: refetchFeature,
  } = useFetchAvailableFeature({ userTournamentId: organizerId });

  const isBroadcastInitialized = useAppSelector(selectIsBroadcastInitialized);
  const isMatchAvailable = useAppSelector(selectIsMatchAvailable);

  const device = useAppSelector(selectDeviceTask);
  const chronology = useAppSelector(selectChronologyTask);
  const matchTime = useAppSelector(selectMatchTimeTask);
  const assets = useAppSelector(selectAssetsTask);
  const template = useAppSelector(selectTemplateTask);
  const retry = useAppSelector(selectReadinessRetry);
  const retryToken = useAppSelector(selectRetryToken);

  const { championship, teams, isSynchronization, isSynchronize } = useGameInfo();
  const gameInfoError = useAppSelector(selectGameInfoError);
  const { activeTemplate, isLoaded: isTemplateLoaded } = useTemplates();
  const { boards } = useBoards();

  const remote = useRemoteConnectionStep(isMatchAvailable === true);

  const involvedPlayers = useMemo(() => {
    if (!teams) return undefined;
    const count = (members?: { isInvolved: boolean }[]) => members?.filter((member) => member.isInvolved).length ?? 0;
    return { first: count(teams.first.members), second: count(teams.second.members) };
  }, [teams]);

  const signals: OverlayReadinessSignals = useMemo(
    () => ({
      routeError,
      subscription: {
        isLoading: isFeatureLoading,
        error: featureError ? ((featureError as Error).message ?? String(featureError)) : undefined,
        hasObsIntegration: Boolean(feature?.OBS_INTEGRATION),
      },
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      matchData: { isLoading: isSynchronization, isLoaded: isSynchronize, error: gameInfoError },
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship: championship ? { name: championship.name, hasTemplate: Boolean(championship.overlayId) } : undefined,
      isTemplateLoaded,
      templateName: activeTemplate?.name,
      boardsCount: boards.length,
      remote,
      teamNames: teams ? { first: teams.first.name, second: teams.second.name } : undefined,
      retry,
    }),
    [
      routeError,
      isFeatureLoading,
      featureError,
      feature?.OBS_INTEGRATION,
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      isSynchronization,
      isSynchronize,
      gameInfoError,
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship,
      isTemplateLoaded,
      activeTemplate?.name,
      boards.length,
      remote,
      teams,
      retry,
    ],
  );

  const onRetry = useCallback(() => dispatch(retryRequested()), [dispatch]);

  const state = useMemo(() => buildReadinessSteps(signals), [signals]);

  // Подписка живёт в react-query с бессрочным кэшем (`staleTime: Infinity, retry: false`),
  // поэтому её сбой сам не рассасывается: без явного `refetch` шаг оставался красным
  // навсегда, даже когда сеть уже вернулась и все запросы отвечали 200.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetchFeature();
  }, [retryToken, refetchFeature]);

  const hasGoneLive = useAppSelector(selectHasGoneLive);

  useRetryDriver(state, useAppSelector(selectRetryAt), hasGoneLive);
  useExitDelay(state.isReady, hasGoneLive);

  const isOnAir = state.isReady || hasGoneLive;

  return useMemo(() => ({ ...state, onRetry, isScreenVisible: !hasGoneLive, isOnAir }), [state, onRetry, hasGoneLive, isOnAir]);
}

/**
 * Автоповтор упавшего шага: назначает попытку, ведёт её по таймеру и снимает, когда шаг
 * починился сам. Живёт рядом с чеклистом, а не в загрузчике, потому что упасть может
 * что угодно — подписка, оформление, данные матча, — а видит их все только чеклист.
 *
 * Работает только до выхода в эфир. Повтор — операция не бесплатная: он передёргивает
 * `setIsBroadcastInitialized(false)`, переподписывает realtime-каналы и заново тянет
 * данные матча, журнал, время и все картинки. Делать это посреди эфира нельзя, тем более
 * молча — чеклиста, который бы это объяснил, там уже нет. В эфире восстановлением
 * занимаются штатные механизмы: realtime-подписка и опрос журнала раз в 3 секунды.
 */
function useRetryDriver(state: OverlayReadinessState, retryAt: number | undefined, hasGoneLive: boolean) {
  const dispatch = useAppDispatch();

  const failedStep = state.steps.find((step) => step.status === 'failed');
  const isRetryNeeded = Boolean(failedStep?.isRetryable) && !hasGoneLive;

  useEffect(() => {
    if (!isRetryNeeded) {
      if (retryAt !== undefined) dispatch(retryCancelled());
      return;
    }

    if (retryAt === undefined) {
      dispatch(retryScheduled({ now: Date.now() }));
      return;
    }

    const timer = setTimeout(() => dispatch(retryRequested()), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [dispatch, isRetryNeeded, retryAt]);
}

/**
 * Держит экран подготовки ещё немного после готовности — на «Всё готово» и уход анимацией,
 * а по истечении задержки объявляет выход в эфир.
 *
 * Раньше эта задержка была локальным `useState`, который на `!isReady` возвращался в `true`
 * — то есть чеклист был спроектирован возвращаемым. Теперь конец задержки — это событие
 * `wentLive`, и снять его может только смена матча.
 */
function useExitDelay(isReady: boolean, hasGoneLive: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isReady || hasGoneLive) return;

    const timer = setTimeout(() => dispatch(wentLive()), READINESS_EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [dispatch, isReady, hasGoneLive]);
}

export default useOverlayReadiness;
